import React, { useMemo, useCallback } from "react";
import { Table, Badge, Button, Form, Spinner } from "react-bootstrap";
import "./GstinExpandableGrid.module.css"; // Custom CSS for the dashboard


/* ---------------- Utilities ---------------- */

const toNum = (v) => (v == null ? 0 : Number(v) || 0);

/*
function groupByVendor(rows, getVendor) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const v = (getVendor(r) || "UNKNOWN").trim();
    if (!map.has(v)) map.set(v, []);
    map.get(v).push(r);
  });
  return Array.from(map.entries()).map(([vendor, list]) => ({
    vendor,
    rows: list,
  }));
}
*/
function normalizeVendorKey(v) {
  return (v || "")
    .toString()
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[–—]/g, "-")
    .trim()
    .toUpperCase();
}

function groupByVendor(rows, getVendor) {
  const map = new Map();

  (rows || []).forEach((r) => {
    const rawVendor = getVendor(r);
    const vendorKey = normalizeVendorKey(rawVendor) || "UNKNOWN_PARTY";

    if (!map.has(vendorKey)) {
      map.set(vendorKey, {
        vendorKey,                 // ✅ safe key
        vendor: rawVendor || "UNKNOWN PARTY", // display
        rows: [],
      });
    }
    map.get(vendorKey).rows.push(r);
  });

  return Array.from(map.values());
}

/* ===================== MAIN ===================== */

export default function GstinExpandableGrid({
  title,
  side,
  summaryRows = [],
  invoicesByGstin = {},
  expandedGstin,
  loadingGstin,
  onToggleExpand,

  selectedIds = [],
  setSelectedIds,
  activeGstin,
  onAttemptSetActiveGstin,
  config,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const expandedInvoices = expandedGstin
    ? invoicesByGstin[expandedGstin] || []
    : [];

  const vendorGroups = useMemo(
    () => groupByVendor(expandedInvoices, config.getVendor),
    [expandedInvoices, config]
  );

  /* ---------------- Helpers ---------------- */

  const allSelectableIds = useMemo(() => {
    return vendorGroups
      .flatMap((vg) => vg.rows)
      .filter((r) => !config.getLocked?.(r))
      .map(config.getId);
  }, [vendorGroups, config]);

  const allSelectedCount = allSelectableIds.filter((id) =>
    selectedSet.has(id)
  ).length;

  const headerChecked =
    allSelectableIds.length > 0 &&
    allSelectedCount === allSelectableIds.length;

  const headerIndeterminate =
    allSelectedCount > 0 &&
    allSelectedCount < allSelectableIds.length;

  /* ---------------- Selection handlers ---------------- */

  const toggleOne = useCallback(
    (gstin, id, locked) => {
      if (locked) return;
      if (!onAttemptSetActiveGstin(gstin)) return;

      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [setSelectedIds, onAttemptSetActiveGstin]
  );

  const toggleVendor = useCallback(
    (gstin, ids, checked) => {
      if (!onAttemptSetActiveGstin(gstin)) return;

      setSelectedIds((prev) => {
        const set = new Set(prev);
        ids.forEach((id) => (checked ? set.add(id) : set.delete(id)));
        return Array.from(set);
      });
    },
    [setSelectedIds, onAttemptSetActiveGstin]
  );

  const toggleAllInvoices = (gstin, checked) => {
    if (!onAttemptSetActiveGstin(gstin)) return;

    setSelectedIds((prev) => {
      const set = new Set(prev);
      allSelectableIds.forEach((id) =>
        checked ? set.add(id) : set.delete(id)
      );
      return Array.from(set);
    });
  };

  /* ===================== RENDER ===================== */

  return (
    <div>
      {/* TITLE BAR */}
      <div className="d-flex justify-content-between mb-1">
        <strong style={{ fontSize: 13 }}>{title}</strong>
        {activeGstin ? (
          <Badge bg="dark" style={{ fontSize: 10 }}>
            🔒 {activeGstin}
          </Badge>
        ) : (
          <span className="text-muted" style={{ fontSize: 11 }}>
            Select invoice to lock GSTIN
          </span>
        )}
      </div>

      {/* SCROLL AREA */}
      <div style={{ maxHeight: 480, overflowY: "auto" }}>
        <Table size="sm" className="mb-0 invoice-table" style={{ fontSize: 11 }}> 
          {/* GSTIN SUMMARY HEADER */}
          <thead
            className="table-light"
            style={{ position: "sticky", top: 0, zIndex: 2 }}
          >
            <tr>
              <th style={{ width: 24 }} />
              <th>GSTIN</th>
              <th style={{ width: 60 }} className="text-end">
                Inv
              </th>
              <th style={{ width: 120 }} className="text-end">
                Taxable
              </th>
              <th style={{ width: 110 }} className="text-end">
                Tax
              </th>
            </tr>
          </thead>

          <tbody>
            {summaryRows.map((s, idx) => {
              const gstin = (s.GSTIN || "").trim();
              const expanded = expandedGstin === gstin;

              return (
                <React.Fragment key={`${side}_${gstin}_${idx}`}>
                  {/* GSTIN ROW */}
                  <tr className="table-secondary">
                    <td>
                      <Button
                        size="sm"
                        variant="link"
                        style={{ padding: 0 }}
                        onClick={() => onToggleExpand(gstin)}
                      >
                        {expanded ? "▾" : "▸"}
                      </Button>
                    </td>
                    <td>
                      <strong>{gstin}</strong>
                      <div className="text-muted" style={{ fontSize: 10 }}>
                        {s.VENDOR_NAME}
                      </div>
                    </td>
                    <td className="text-end">
                      <Badge bg="light" text="dark">
                        {s.INV_COUNT}
                      </Badge>
                    </td>
                    <td className="text-end">
                      ₹{toNum(s.TOTAL_TAXABLE).toFixed(2)}
                    </td>
                    <td className="text-end">
                      ₹{toNum(s.TOTAL_TAX).toFixed(2)}
                    </td>
                  </tr>

                  {/* EXPANDED INVOICES */}
                  {expanded && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0 }}>
                        {loadingGstin ? (
                          <div className="p-2 text-muted">
                            <Spinner size="sm" /> Loading invoices…
                          </div>
                        ) : (
                          <Table
                            bordered
                            size="sm"
                            className="mb-0"
                            style={{ fontSize: 11 }}
                          >
                            {/* STICKY INVOICE HEADER WITH SELECT ALL */}
                            <thead
                              className="table-light"
                              style={{
                                position: "sticky",
                                top: 32,
                                zIndex: 3,
                              }}
                            >
                              <tr>
                                <th style={{ width: 24 }}>
                                  <Form.Check
                                    checked={headerChecked}
                                    ref={(el) =>
                                      el &&
                                      (el.indeterminate = headerIndeterminate)
                                    }
                                    onChange={(e) =>
                                      toggleAllInvoices(
                                        gstin,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </th>
                                <th style={{ width: 160 }}>Invoice No</th>
                                <th style={{ width: 110 }}>Invoice Date</th>
                                <th
                                  style={{ width: 120 }}
                                  className="text-end"
                                >
                                  Taxable
                                </th>
                                <th
                                  style={{ width: 110 }}
                                  className="text-end"
                                >
                                  Tax
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {vendorGroups.map((vg, vIdx) => {
                                const selectableIds = vg.rows
                                  .filter((r) => !config.getLocked?.(r))
                                  .map(config.getId);

                                const selCount = selectableIds.filter((id) =>
                                  selectedSet.has(id)
                                ).length;

                                const vChecked =
                                  selCount > 0 &&
                                  selCount === selectableIds.length;
                                const vInd =
                                  selCount > 0 &&
                                  selCount < selectableIds.length;

                                const vTaxable = vg.rows.reduce(
                                  (a, r) => a + toNum(config.getTaxable(r)),
                                  0
                                );
                                const vTax = vg.rows.reduce(
                                  (a, r) => a + toNum(config.getTax(r)),
                                  0
                                );
                                const vendorSticky = vg.rows.length > 1;

                                return (
                                    <React.Fragment key={`${side}_${gstin}_${vg.vendorKey}`}>
                                      {/* VENDOR HEADER */}
                                    <tr
                                            style={{
                                              background: "#fafafa",
                                              position: vendorSticky ? "sticky" : "static",
                                              top: vendorSticky ? 60 : undefined,
                                              zIndex: vendorSticky ? 2 : undefined,
                                              borderTop: "2px solid #dee2e6",
                                          
                                            }}
                                          >
                                      <td>
                                        <Form.Check
                                          checked={vChecked}
                                          ref={(el) =>
                                            el && (el.indeterminate = vInd)
                                          }
                                          onChange={(e) =>
                                            toggleVendor(
                                              gstin,
                                              selectableIds,
                                              e.target.checked
                                            )
                                          }
                                        />
                                      </td>
                                      <td colSpan={2} style={{ fontWeight: 700 }}>
                                        {vg.vendor}
                                        <span
                                          className="text-muted ms-1"
                                          style={{ fontSize: 10 }}
                                        >
                                          ({vg.rows.length})
                                        </span>
                                      </td>
                                      <td
                                        className="text-end"
                                        style={{ fontWeight: 700 }}
                                      >
                                        ₹{vTaxable.toFixed(2)}
                                      </td>
                                      <td
                                        className="text-end"
                                        style={{ fontWeight: 700 }}
                                      >
                                        ₹{vTax.toFixed(2)}
                                      </td>
                                    </tr>

                                    {/* INVOICE ROWS */}
                                    {vg.rows.map((r) => {
                                      const id = config.getId(r);
                                      const locked = !!config.getLocked?.(r);
                                      const checked = selectedSet.has(id);

                                      return (
                                        <tr
                                          key={id}
                                          className={
                                            checked ? "table-info" : ""
                                          }
                                          style={
                                            locked
                                              ? { opacity: 0.55 }
                                              : undefined
                                          }
                                        >
                                          <td>
                                            <Form.Check
                                              disabled={locked}
                                              checked={checked}
                                              onChange={() =>
                                                toggleOne(gstin, id, locked)
                                              }
                                            />
                                          </td>
                                          <td>{config.getInvNo(r)}</td>
                                          <td>{config.getInvDate(r)}</td>
                                          <td className="text-end">
                                            ₹
                                            {toNum(
                                              config.getTaxable(r)
                                            ).toFixed(2)}
                                          </td>
                                          <td className="text-end">
                                            ₹
                                            {toNum(config.getTax(r)).toFixed(
                                              2
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </Table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {!summaryRows.length && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  No unmatched GSTINs found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
